import { useState } from 'react';
import { Color, Size, documentFileMatches, t, type DateFormat, type DocumentAttachmentKind, type DocumentFileRow, type DocumentMailRow, type LeftInZohoFile, type PagedRecord } from '@bms2/shared';
import { Button, TextInput } from '../../components/controls';
import { FileRow, type FileRowProps } from '../../components/composites/FileRow';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { DocumentFromMail, DocumentFromMailRead } from './DocumentFromMail';
import { useFileRowModals } from './FileRowModals';
import { RecordUploadModal } from './RecordUploadModal';
import { fileScanOf, fileVerifiedOf, releasePillFor } from './tileItems';
import { LeftInZohoFiles } from './LeftInZohoFiles';
import { FILES_PATH } from './api';
import type { ReleaseCompanies } from './releaseFiles';
import styles from './DocumentPage.module.css';

/**
 * The files attached to a document, on the document's own page (decision
 * 0457).
 *
 * 🔒 A record page draws its files through the Files tile. A document page has
 * no tile canvas, so it draws this panel instead — the same rows, the same
 * upload modal, the same file types. 🔒 Each row is the one File row the tile
 * draws (decision 3010, amending 0457): the name opens the file through the
 * proxy in its own tab, and Share and the pencil open the windows a tile's row
 * opens, offered while the viewer may write the document.
 *
 * 🔒 The upload is offered exactly while the viewer may write the document,
 * on every document page alike: the panel reads the document's own write
 * refusal and decides, and no page passes a rule of its own (T2212; the
 * owner's word of 2026-10-11). A sent order is exactly when the customer's own
 * purchase order arrives, so the upload is never gated on the document being
 * editable the way its lines are.
 */
export interface DocumentFilesPanelProps {
  recordType: PagedRecord | DocumentAttachmentKind;
  recordId: string;
  files: readonly DocumentFileRow[];
  apiBaseUrl?: string;
  onChanged: () => void;
  /** A sentence naming what this document is waiting for, drawn above the list; absent where it waits for nothing. */
  waitingFor?: string | null;
  /**
   * 🔒 The document write gate's refusal for this viewer, as the page's answer
   * carries it (decision 0687); null offers the upload, and a sentence withholds
   * it, because the server refuses that viewer's upload either way.
   */
  writeRefusal: string | null;
  /**
   * 🔒 The messages tagged to this document, drawn beneath the files as From mail
   * (decision 1663), where the page's own read already carried them; absent, the
   * panel reads them itself.
   */
  mail?: readonly DocumentMailRow[];
  /** 🔒 The files the record's Zoho Books source keeps there (decision 4805), drawn under the files, or in place of the empty state; absent or empty draws nothing. */
  leftInZoho?: readonly LeftInZohoFile[];
  /**
   * 🔒 Decision 11480: the companies the Release window lists, handed in by a document whose kind the release rules
   * name (the Bill, the Purchase Order); present, each row offers Release before Share to a viewer who writes the document.
   */
  releaseTo?: ReleaseCompanies | null;
}

export function DocumentFilesPanel({ recordType, recordId, files, apiBaseUrl = '', onChanged, waitingFor = null, writeRefusal, mail, leftInZoho = [], releaseTo = null }: DocumentFilesPanelProps) {
  const [uploading, setUploading] = useState(false);
  const [typed, setTyped] = useState({ owner: '', text: '' });
  // 🔨 The text belongs to the record it was typed on: a change of record clears it (T2005; decision 2713).
  const owner = `${recordType}:${recordId}`;
  const filter = typed.owner === owner ? typed.text : '';
  // 🔨 Moving A to B and back to A shows no text: the state is reset on the first draw of a new record, not kept until someone types (T2023).
  if (typed.owner !== owner) setTyped({ owner, text: '' });
  const setFilter = (text: string) => setTyped({ owner, text });
  // 🔨 The box is drawn from two files up, so at one file or fewer the text counts as empty, and a list never reads "No file here matches" with no box to clear it (T2005).
  const filterDrawn = files.length > 1;
  // 🔒 The filter matches each file's name and the name its served version is stored under (T1741; decision 2713).
  const shown = files.filter((file) => documentFileMatches(file, filterDrawn ? filter : ''));
  const zone = useViewerZone();
  const dateFormat = useDateFormat();
  const rowModals = useFileRowModals(apiBaseUrl, onChanged, releaseTo);

  return (
    <div data-document-files={recordId} data-document-file-count={files.length}>
      {/* 🔒 What this document is waiting for, in the person's words, above the list it is waiting on (decision 0457). */}
      {waitingFor ? <p className={styles.rejection} data-document-files-waiting="true">{waitingFor}</p> : null}
      {files.length === 0 && leftInZoho.length === 0 ? <p className={styles.muted}>{t('documentFiles.none')}</p> : null}
      {/* 🔨 Drawn once there are two files to tell apart; one file needs no filter (T1741; decision 2713). */}
      {filterDrawn ? (
        <div data-document-files-filter="true">
          <TextInput size={Size.Medium} label={t('documentFiles.filter')} value={filter} onChange={setFilter} type="search" name="documentFilesFilter" />
        </div>
      ) : null}
      {files.length > 0 && shown.length === 0 ? <p className={styles.muted} data-document-files-no-match="true">{t('documentFiles.noMatch')}</p> : null}
      <ul className={styles.fileList}>
        {shown.map((file) => (
          <FileRow
            key={file.id}
            {...fileRowOf(file, { apiBaseUrl, zone, dateFormat, writes: writeRefusal === null, share: rowModals.share, details: rowModals.details, release: releaseTo ? rowModals.release : null })}
          />
        ))}
      </ul>
      <LeftInZohoFiles files={leftInZoho} />
      {rowModals.modals}
      {writeRefusal === null ? (
        <div className={styles.rowAction} data-document-files-upload="true">
          <Button size={Size.Small} color={Color.Navy} text={[t('documentFiles.upload')]} actions={[() => setUploading(true)]} />
        </div>
      ) : null}
      {/* 🔒 The mail tagged to this document, beneath the uploaded files and read-only (decision 1663). */}
      {mail === undefined ? <DocumentFromMailRead recordType={recordType} recordId={recordId} apiBaseUrl={apiBaseUrl} /> : <DocumentFromMail rows={mail} />}
      {/* 🔨 Unmounted when it closes, so reopening it does not hold the last file's type. */}
      {uploading ? (
        <RecordUploadModal
          open
          recordType={recordType}
          recordId={recordId}
          apiBaseUrl={apiBaseUrl}
          onCancel={() => setUploading(false)}
          onSaved={() => { setUploading(false); onChanged(); }}
        />
      ) : null}
    </div>
  );
}

/**
 * 🔒 A document's file as the one File row draws it (decision 3010): the type as
 * the chip, the version past the first beside it, the description as the third
 * line or the name's title (decision 3013),
 * the quarantine sentence on its own line and the restriction pill, as a Files
 * tile's row reads off the same file; the scan's mark and the Verified line as
 * decision 3012 draws them.
 */
export function fileRowOf(
  file: DocumentFileRow,
  context: { apiBaseUrl: string; zone: string; dateFormat: DateFormat; writes: boolean; share: (file: { id: string; name: string }) => void; details: (file: { id: string; name: string; description: string; version: number | null; typeName: string | null }) => void; release?: ((file: { id: string; name: string }) => void) | null },
): FileRowProps {
  const quarantined = Boolean(file.quarantine);
  const share = { label: t('file.share'), onAct: () => context.share({ id: file.id, name: file.filename }) };
  const release = context.release;
  return {
    name: file.filename,
    href: `${context.apiBaseUrl}${FILES_PATH}/${file.id}/stream`,
    mimeType: file.mimeType ?? null,
    chip: file.fileTypeName || null,
    // 🔒 Decision 3013: the description and the version, which the row places itself.
    description: file.description || null,
    version: file.versions,
    // 🔒 Decision 3012: the shield under a clean file's icon, Scan pending on a waiting one, and the Verified line on a verified one.
    scan: quarantined ? null : fileScanOf(file.scanVerdict ?? null, file.scannedAt ?? null, file.scannedBy ?? null, context.zone, context.dateFormat),
    verified: file.verificationStatus === 'Verified' ? fileVerifiedOf(file.verifiedBy ?? null, file.verifiedAt ?? null, context.zone, context.dateFormat) : null,
    quarantine: file.quarantine ?? null,
    // 🔒 Decision 7420: the lock beside the name of a file whose bytes are stored encrypted.
    encrypted: file.encrypted === true,
    flag: file.restriction ?? null,
    // 🔒 Decision 1182: a quarantined file says so. Since decision 3012 no other file carries a pill; the Verified line says it.
    pill: quarantined ? { text: t('quarantine.pill'), group: 'error' } : releasePillFor(file.releaseApproval),
    // 🔒 Decision 11480: Release first and Share second where the document's kind offers Release, as a Deal's file row draws them (decision 6040).
    button: !context.writes ? null : release ? { label: t('files.release.button'), onAct: () => release({ id: file.id, name: file.filename }), icon: 'forward' } : share,
    secondButton: context.writes && release ? share : null,
    onEdit: context.writes ? () => context.details({ id: file.id, name: file.filename, description: file.description ?? '', version: file.versions, typeName: file.fileTypeName || null }) : null,
    data: { 'data-document-file': file.id, 'data-document-file-type': file.fileTypeName },
  };
}
